import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  RefreshCwIcon,
  XIcon
} from 'lucide-react';
import type { ComponentProps } from 'react';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';
import { toast } from 'sonner';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

import {
  type Json,
  type SduiItem,
  SduiItemsRenderer
} from './SduiItemsRenderer';

import '@/lib/pdfWorker';

import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle
} from '@/components/Dialog';
import { Button } from '@/components/ui/button';

interface OutlineEntry {
  pageNumber: number;
  title: string;
}

type PDFDoc = Parameters<
  NonNullable<ComponentProps<typeof Document>['onLoadSuccess']>
>[0];

const SDUI_PAGE_PAYLOAD = gql`
  query SduiPagePayload($opportunityId: ID!, $pageKey: String!) {
    sduiPagePayload(opportunityId: $opportunityId, pageKey: $pageKey) {
      items
    }
  }
`;

class PdfRef {
  gen: number;
  num: number;

  constructor({ gen, num }: { gen: number; num: number }) {
    this.gen = gen;
    this.num = num;
  }

  toString(): string {
    let str = `${String(this.num)}R`;
    if (this.gen !== 0) {
      str += String(this.gen);
    }
    return str;
  }
}

export default function ReportEditorDialog({
  fileData,
  filename,
  onOpenChange,
  onRefetchReport,
  open,
  opportunityId
}: {
  fileData: null | string;
  filename: string;
  onOpenChange: (open: boolean) => void;
  onRefetchReport?: () => unknown;
  open: boolean;
  opportunityId: string;
}) {
  const [page, setPage] = useState<number>(1);
  const [numPages, setNumPages] = useState<null | number>(null);
  const [loadError, setLoadError] = useState<null | string>(null);
  const [outlineEntries, setOutlineEntries] = useState<OutlineEntry[]>([]);
  const [rightPanelWidth, setRightPanelWidth] = useState<number>(0);
  const [rightPanelEl, setRightPanelEl] = useState<HTMLDivElement | null>(null);
  const [isRefetchingReport, setIsRefetchingReport] = useState(false);
  const [pdfRenderKey, setPdfRenderKey] = useState(0);
  const editedFieldValuesRef = useRef<Record<string, Json>>({});
  const titleForCurrentPage = useMemo(() => {
    const fromOutline = findOutlineTitleForPage(outlineEntries, page);
    return ((fromOutline ?? `Page ${String(page)}`) || '').trim();
  }, [outlineEntries, page]);
  const {
    data: sduiData,
    error: sduiError,
    loading: sduiLoading
  } = useQuery<{
    sduiPagePayload?: { items?: unknown };
  }>(SDUI_PAGE_PAYLOAD, {
    skip: !open || !opportunityId,
    variables: {
      opportunityId,
      pageKey: titleForCurrentPage
    }
  });

  const sduiItems = useMemo(() => {
    const raw = sduiData?.sduiPagePayload?.items;
    if (!Array.isArray(raw)) return [];
    return raw as SduiItem[];
  }, [sduiData]);

  const handleSduiFieldChange = useCallback((path: string, value: Json) => {
    editedFieldValuesRef.current[path] = value;
  }, []);

  const decoded = useMemo(() => {
    if (!fileData)
      return {
        error: null as null | string,
        file: null as { data: Uint8Array } | null
      };
    return decodeBase64ToPdfFile(fileData);
  }, [fileData]);

  const pdfError = decoded.error ?? loadError;

  const handleRefetchReport = useCallback(async () => {
    try {
      setIsRefetchingReport(true);
      setLoadError(null);
      await onRefetchReport?.();
    } catch (error) {
      console.error('Failed to refetch report:', error);
      toast.error('Failed to refetch report');
    } finally {
      setIsRefetchingReport(false);
      setPdfRenderKey(k => k + 1);
    }
  }, [onRefetchReport]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setPage(1);
      setNumPages(null);
      setLoadError(null);
      setOutlineEntries([]);
      setRightPanelWidth(0);
      setIsRefetchingReport(false);
      setPdfRenderKey(0);
      editedFieldValuesRef.current = {};
    }

    onOpenChange(nextOpen);
  };

  useLayoutEffect(() => {
    if (!open) return;
    if (!rightPanelEl) return;

    const update = () => {
      setRightPanelWidth(rightPanelEl.clientWidth);
    };

    // Measure after layout + after Radix open animation tick.
    update();
    const raf = requestAnimationFrame(update);

    const ro = new ResizeObserver(() => {
      update();
    });
    ro.observe(rightPanelEl);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [open, rightPanelEl]);

  return (
    <Dialog
      onOpenChange={handleOpenChange}
      open={open}
    >
      <DialogContent
        aria-describedby={undefined}
        className="flex h-[80vh] max-h-[80vh] w-[80vw] max-w-none flex-col overflow-hidden p-0 outline-none"
        showCloseButton={false}
      >
        <DialogBody>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="flex shrink-0 items-center justify-between gap-4 border-b px-4 py-3">
              <div className="min-w-0">
                <DialogTitle className="truncate">
                  {filename || 'Edit Report'}
                </DialogTitle>
                <DialogDescription className="truncate">
                  Edit view
                </DialogDescription>
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="flex items-center gap-2">
                  <Button
                    disabled={page <= 1}
                    onClick={() => {
                      setPage(p => Math.max(1, p - 1));
                    }}
                    size="icon"
                    variant="outline"
                  >
                    <ChevronLeftIcon />
                  </Button>
                  <div className="text-muted-foreground text-sm whitespace-nowrap">
                    Page {page}
                    {numPages ? ` / ${String(numPages)}` : ''}
                  </div>
                  <Button
                    disabled={numPages ? page >= numPages : false}
                    onClick={() => {
                      setPage(p =>
                        numPages ? Math.min(numPages, p + 1) : p + 1
                      );
                    }}
                    size="icon"
                    variant="outline"
                  >
                    <ChevronRightIcon />
                  </Button>
                  <DialogClose asChild>
                    <Button
                      aria-label="Close"
                      className="-mr-1"
                      size="icon"
                      variant="ghost"
                    >
                      <XIcon className="size-4" />
                    </Button>
                  </DialogClose>
                </div>
                <Button
                  disabled={!onRefetchReport || isRefetchingReport || !open}
                  onClick={() => void handleRefetchReport()}
                  size="sm"
                  variant="outline"
                >
                  <RefreshCwIcon
                    className={`mr-2 size-4 ${isRefetchingReport ? 'animate-spin' : ''}`}
                  />
                  Refetch report
                </Button>
              </div>
            </div>
            <div className="flex min-h-0 flex-1 overflow-hidden">
              <div className="bg-muted/10 flex min-h-0 w-[35%] flex-col overflow-hidden border-r">
                <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto p-3">
                  <div className="mt-3">
                    {sduiLoading ? (
                      <div className="text-muted-foreground text-sm">
                        Loading fields...
                      </div>
                    ) : sduiError ? (
                      <div className="text-muted-foreground text-sm">
                        Failed to load fields: {sduiError.message}
                      </div>
                    ) : (
                      <SduiItemsRenderer
                        currentPageTitle={titleForCurrentPage}
                        items={sduiItems}
                        onChange={handleSduiFieldChange}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div
                className="min-w-0 flex-1 overflow-auto bg-white p-4"
                ref={setRightPanelEl}
              >
                {pdfError ? (
                  <div className="text-muted-foreground flex h-full items-center justify-center text-sm">
                    {pdfError}
                  </div>
                ) : decoded.file ? (
                  <div className="flex min-h-full w-full items-center justify-center">
                    <Document
                      error={
                        <div className="text-muted-foreground text-sm">
                          Failed to load report preview
                        </div>
                      }
                      file={decoded.file}
                      key={String(pdfRenderKey)}
                      loading={
                        <div className="text-muted-foreground text-sm">
                          Loading report...
                        </div>
                      }
                      noData={
                        <div className="text-muted-foreground text-sm">
                          No report loaded
                        </div>
                      }
                      onLoadError={error => {
                        console.error('Failed to load report PDF:', error);
                        setLoadError('Failed to load report preview');
                        toast.error('Failed to load report preview');
                      }}
                      onLoadSuccess={pdf => {
                        setNumPages(pdf.numPages);
                        setPage(p => Math.min(pdf.numPages, p));
                        setLoadError(null);
                        loadOutlineEntries(pdf)
                          .then(({ entries }) => {
                            setOutlineEntries(entries);
                          })
                          .catch(() => {
                            setOutlineEntries([]);
                          });
                      }}
                      onSourceError={error => {
                        console.error(
                          'Failed to read report PDF source:',
                          error
                        );
                        setLoadError('Failed to read report preview source');
                        toast.error('Failed to load report preview');
                      }}
                    >
                      <Page
                        pageNumber={page}
                        renderAnnotationLayer={false}
                        renderTextLayer={false}
                        width={Math.max(720, (rightPanelWidth || 0) - 32)}
                      />
                    </Document>
                  </div>
                ) : (
                  <div className="text-muted-foreground flex h-full items-center justify-center text-sm">
                    No report loaded
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

function decodeBase64ToPdfFile(fileData: string): {
  error: null | string;
  file: { data: Uint8Array } | null;
} {
  const trimmed = fileData.trim();
  const sanitizeBase64 = (value: string) => value.replace(/\s/g, '');

  const base64Only = (() => {
    if (!trimmed.startsWith('data:')) return sanitizeBase64(trimmed);
    const match = /^data:[^;]+;base64,([\s\S]*)$/.exec(trimmed);
    return sanitizeBase64(match?.[1] ?? trimmed);
  })();

  try {
    const binaryString = atob(base64Only);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i += 1) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return { error: null, file: { data: bytes } };
  } catch (error) {
    console.error('Failed to decode report PDF base64:', error);
    return { error: 'Failed to decode report preview', file: null };
  }
}

function findOutlineTitleForPage(
  entries: OutlineEntry[],
  pageNumber: number
): null | string {
  if (!entries.length) return null;
  let lo = 0;
  let hi = entries.length - 1;
  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    const midPage = entries[mid]?.pageNumber ?? 0;
    if (midPage === pageNumber) {
      return entries[mid]?.title ?? null;
    }
    if (midPage < pageNumber) {
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return null;
}

async function loadOutlineEntries(
  pdf: PDFDoc
): Promise<{ entries: OutlineEntry[]; outlineCount: number }> {
  const outline = await pdf.getOutline();
  const outlineCount = outline.length;
  if (outlineCount === 0) return { entries: [], outlineCount };

  type OutlineItem = (typeof outline)[number];
  // Page-centric: one title per page, based on the page destination.
  // If multiple outline nodes resolve to the same page, keep the first one we encounter.
  const byPage = new Map<number, OutlineEntry>();

  const visit = async (nodes: OutlineItem[]) => {
    for (const node of nodes) {
      try {
        const title = (node.title || '').trim();
        if (title) {
          const dest =
            typeof node.dest === 'string'
              ? await pdf.getDestination(node.dest)
              : node.dest;
          if (dest && Array.isArray(dest) && dest.length > 0) {
            const first = dest[0] as unknown;
            const pageIndex =
              typeof first === 'number'
                ? first
                : await pdf.getPageIndex(
                    new PdfRef(first as { gen: number; num: number })
                  );

            const pageNumber = pageIndex + 1;
            if (!byPage.has(pageNumber)) {
              byPage.set(pageNumber, { pageNumber, title });
            }
          }
        }
      } catch {
        // ignore outline nodes that fail to resolve
      }

      if (node.items.length) {
        await visit(node.items as OutlineItem[]);
      }
    }
  };

  await visit(outline);

  const entries = [...byPage.values()].sort(
    (a, b) => a.pageNumber - b.pageNumber
  );
  return { entries, outlineCount };
}
